import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { producaoApi } from '@/lib/api';
import type { OrdemProducao } from '@/types';

export function useOrdensProducao(params?: {
  status?: string;
  maquinaId?: string;
  produtoId?: string;
}) {
  return useQuery({
    queryKey: ['ordens-producao', params],
    queryFn: async () => {
      const response = await producaoApi.listar(params);
      const data = response.data;
      if (data && typeof data === 'object' && 'data' in data && Array.isArray(data.data)) {
        return data.data as OrdemProducao[];
      }
      return (Array.isArray(data) ? data : []) as OrdemProducao[];
    },
  });
}

export function useOrdemProducao(id: string) {
  return useQuery({
    queryKey: ['ordem-producao', id],
    queryFn: async () => {
      const response = await producaoApi.buscar(id);
      return response.data as OrdemProducao;
    },
    enabled: !!id,
  });
}

export function useCriarOrdemProducao() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: { produtoId: string; maquinaId: string; quantidadeSolicitada: number }) =>
      producaoApi.criar(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ordens-producao'] });
    },
  });
}

export function useIniciarProducao() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, operadorId }: { id: string; operadorId?: string }) =>
      producaoApi.iniciar(id, operadorId),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['ordens-producao'] });
      queryClient.invalidateQueries({ queryKey: ['ordem-producao', id] });
      queryClient.invalidateQueries({ queryKey: ['maquinas'] });
    },
  });
}

export function useFinalizarProducao() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      producaoApi.finalizar(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['ordens-producao'] });
      queryClient.invalidateQueries({ queryKey: ['ordem-producao', id] });
      queryClient.invalidateQueries({ queryKey: ['maquinas'] });
      queryClient.invalidateQueries({ queryKey: ['produtos'] });
    },
  });
}

export function useRegistrarRefugo() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      producaoApi.registrarRefugo(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['ordens-producao'] });
      queryClient.invalidateQueries({ queryKey: ['ordem-producao', id] });
    },
  });
}

export function useFilaProducao(maquinaId: string) {
  return useQuery({
    queryKey: ['fila-producao', maquinaId],
    queryFn: async () => {
      const response = await producaoApi.filaSimplificada(maquinaId);
      const data = response.data;
      if (data && typeof data === 'object' && 'data' in data && Array.isArray(data.data)) {
        return data.data as OrdemProducao[];
      }
      return (Array.isArray(data) ? data : []) as OrdemProducao[];
    },
    enabled: !!maquinaId,
    refetchInterval: 30000, // Atualiza a cada 30 segundos
  });
}

export function useEstatisticasProducao() {
  return useQuery({
    queryKey: ['producao-estatisticas'],
    queryFn: async () => {
      const response = await producaoApi.estatisticas();
      return response.data;
    },
  });
}
